'use client'
import Link from "next/link";
import Style from "@/lib/style";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import * as EmailValidator from 'email-validator';
type Props = {
  programName: string;
};

export default function ContactForm(props: Props) {
  const { programName } = props;
  const matchedStyle = Style.find(style => style.title === programName);

  const getProgramDisplayName = (program: string): string => {
    switch (program) {
      case "basico":
        return "Básico";
      case "medio":
        return "Medio";
      case "profesional":
        return "Profesional";
      case "panaderia-y-pasteleria":
        return "Panadería y Pastelería";
      case "mixologia":
        return "Mixología";
      case "barismo":
        return "Barismo";
      default:
        return "";
    }
  }

  return (
    <div className="md:bigscreen-responsive max-w-md m-0 md:sticky block top-10 md:left-[50%] md:translate-x-[-626px] z-50">
      <div className="absolute -top-8 left-6 z-10 bg-[#231F20] rounded-bl-[55px] rounded-tr-[55px] py-7 px-5">
        <img src="/images/logos/escudo.svg" className="h-16 w-16 opacity-90" alt="Escudo Amura" />
      </div>
      <div className="rounded-tr-[80px] rounded-tl-xl rounded-b-xl shadow-xl overflow-hidden">
        <div className={`${matchedStyle?.colors?.[0].colorSection} w-full text-white pt-4`}>
          <h5 className="ml-36 text-xl font-bold my-2">¡Contactanos!</h5>
          <div className={`${matchedStyle?.colors?.[0].colorSectionDark} w-96 text-white text-md rounded-tr-[100px] rounded-tl-md py-2`}>
            <h6 className="ml-36 font-thin">Sacá tu cita hoy mismo</h6>
          </div>
        </div>
        <div className="bg-white font-thin text-center text-md py-8 px-8">
          <p>Dejanos tus datos para coordinar una visita a nuestras instalaciones o para evacuar tus dudas</p>
          <Formik
            validateOnBlur={false}
            validateOnMount={false}
            validateOnChange={true}
            initialValues={{
              firstName: "",
              lastName: "",
              email: "",
              code: "506",
              phone: "",
              description: "",
              program: getProgramDisplayName(programName),
              confirm: false,
              source: "Amura.cr",
              submittedAt: new Date().toISOString(),
              status: 'N'
            }}
            validationSchema={Yup.object({
              firstName: Yup.string()
                .required("El nombre es requerido"),
              lastName: Yup.string()
                .required("El apellido es requerido"),
              email: Yup.string()
                .email("Correo inválido")
                .required("El correo es requerido")
                .test("email", "El correo no cuenta con un formato válido", (value) => {
                  return EmailValidator.validate(value);
                }),
              phone: Yup.string()
                .required("El número de teléfono es requerido"),
              confirm: Yup.bool()
                .oneOf([true], "Debes aceptar la política de privacidad")
            })}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              try {
                setSubmitting(false);

                const leadData = {
                  first_name: values.firstName,
                  last_name: values.lastName,
                  email: values.email,
                  phone: values.code + values.phone,
                  program: values.program,
                  source: values.source,
                  submitted_at: values.submittedAt,
                  status: values.status,
                  message: values.description
                };

                const response = await fetch('/api/leads', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(leadData),
                });

                if (!response.ok) {
                  const errorData = await response.json().catch(() => ({}));
                  throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
                }

                alert('Gracias por contactarnos, pronto nos pondremos en contacto con usted');
                resetForm();
              } catch (error) {
                console.error(error);       
                alert('Hubo un error al enviar su mensaje, por favor inténtelo de nuevo');         
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({isSubmitting, errors}) => (
              <Form>
                <div className="flex flex-col justify-between gap-4 py-8">
                  <div className="flex gap-4">
                    <Field type="text" id="firstName" name="firstName" placeholder="Nombre" className={`rounded-lg border-2  w-[50%] px-2 py-3 placeholder:italic ${errors.firstName ? 'border-red-500' : 'border-[#e1e1e1]'}`} />
                    <Field type="text" id="lastName" name="lastName" placeholder="Apellido" className={`rounded-lg border-2 w-[50%] px-2 py-3 placeholder:italic ${errors.lastName ? 'border-red-500' : 'border-[#e1e1e1]'}`} />
                  </div>
                  <div>
                    <Field type="email" id="email" name="email" placeholder="Correo electrónico" className={`rounded-lg border-2 w-full px-2 py-3 placeholder:italic ${errors.email ? 'border-red-500' : 'border-[#e1e1e1]'}`}  />
                  </div>
                  <span className="text-left text-xs font-thin">Teléfono celular:</span>
                  <div className="flex gap-4">
                    <Field as="select" id="code" name="code" className={`bg-gray-100 rounded-lg w-24 px-3 py-3 ${errors.code ? 'border-red-500' : 'border-[#e1e1e1]'}`}>
                      <option value="506">+506</option>
                      <option value="507">+507</option>
                      <option value="502">+502</option>
                      <option value="503">+503</option>
                      <option value="504">+504</option>
                      <option value="505">+505</option>
                    </Field>
                    <Field type="text" id="phone" name="phone" placeholder="88888888" className={`rounded-lg border-2 w-full px-2 py-3 placeholder:italic ${errors.phone ? 'border-red-500' : 'border-[#e1e1e1]'}`} />
                  </div>
                  <Field as="textarea" id="description" name="description" placeholder="Escriba su mensaje" className="rounded-lg border-2 w-full h-full px-2 py-3 border-[#e1e1e1]" />
                  <div className="flex items-center py-4">
                    <Field type="checkbox" id="confirm" name="confirm" className={`h-5 w-5 border-2 ${errors.confirm ? 'border-red-500' : 'border-red-500'}`} />
                    <label htmlFor="checkbox" className="ml-2 text-xs text-gray-700">
                      He leído y aceptado la <Link href="" className="font-bold hover:text-gray-400">Política de Privacidad</Link>
                    </label>
                  </div>
                  <button className={`${matchedStyle?.colors?.[0].buttonGradient} text-white font-bold rounded-full py-3 px-4 shadow-sm hover:shadow-lg md:text-nowrap`}
                    type="submit"
                    disabled={isSubmitting}
                  >
                    Enviar consulta
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}